import { useBusinessContext } from "../BusinessContext";
import { personScopeUrl } from "./personScope";
import { formatPersonName } from "./personName";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import Save from "@mui/icons-material/Save";
import api from "../api/axios";
import DialogDateField from "./DialogDateField";
import LoadingIndicator from './LoadingIndicator';
import { normalizeDialogDate } from "./dialogDate";


const localToday = () => {
  const today = new Date();
  const offset = today.getTimezoneOffset() * 60000;
  return new Date(today.getTime() - offset).toISOString().slice(0, 10);
};

const dateValue = (value) => {
  if (!value) return localToday();
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

const BestellungDialog = ({
  open,
  onClose,
  onSaved,
  customer = null,
  customers = [],
  order = null,
  loading = false,
}) => {
  const { mandantId, betriebId } = useBusinessContext();
  const [saving, setSaving] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [bestelldatum, setBestelldatum] = useState(localToday());
  const [bestellung, setBestellung] = useState("");
  const [erledigt, setErledigt] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setCustomerId(String(order?.kunden_zuordnung_id ?? order?.Kunden_Id ?? customer?.KundenZuordnung_Id ?? customer?.kunden_zuordnung_id ?? ""));
    setBestelldatum(dateValue(order?.Bestelldatum));
    setBestellung(order?.Bestellung ?? "");
    setErledigt(Boolean(order?.Erledigt));
    setError("");
  }, [open, order, customer]);

  const savePayload = async (payload) => {
    if (saving) return;
    setSaving(true);
    try {
      const response = order
        ? await api.put(personScopeUrl(`/bestellungen/${order.Id}`, mandantId, betriebId), payload)
        : await api.post(personScopeUrl("/bestellungen", mandantId, betriebId), payload);
      onSaved(response.data);
      onClose();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error || "Bestellung konnte nicht gespeichert werden."
      );
    } finally { setSaving(false); }
  };

  const handleSave = () => {
    const payload = {
      kunden_zuordnung_id: Number(customerId),
      Bestelldatum: normalizeDialogDate(bestelldatum),
      Bestellung: bestellung,
      ...(order ? { Erledigt: erledigt } : {}),
    };
    savePayload(payload);
  };

  const selectedCustomer = customer || (order ? { Name: order.Kunde_Name, Vorname: order.Kunde_Vorname } : null) || customers.find(
    (entry) => String(entry.Id) === customerId
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{order ? "Bestellung bearbeiten" : "Neue Bestellung"}</DialogTitle>
      <DialogContent>
        {loading && <LoadingIndicator label="Kunden werden geladen …" />}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {customer || order ? (
          <TextField
            fullWidth
            size="small"
            label="Kunde"
            value={selectedCustomer
              ? formatPersonName(selectedCustomer.Name ?? selectedCustomer.Kunde_Name, selectedCustomer.Vorname ?? selectedCustomer.Kunde_Vorname)
              : ""}
            disabled
          />
        ) : (
          <TextField
            select
            fullWidth
            size="small"
            label="Kunde"
            value={customerId}
            onChange={(event) => setCustomerId(event.target.value)}
          >
            {customers.map((entry) => (
              <MenuItem key={entry.Id} value={String(entry.Id)}>
                {formatPersonName(entry.Name, entry.Vorname)}
              </MenuItem>
            ))}
          </TextField>
        )}
        <DialogDateField
          fullWidth
          type="date"
          size="small"
          label="Bestelldatum"
          value={bestelldatum}
          onChange={(event) => setBestelldatum(event.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="Bestellung"
          value={bestellung}
          onChange={(event) => setBestellung(event.target.value)}
        />
        {order && (
          <FormControlLabel
            control={(
              <Checkbox
                checked={erledigt}
                onChange={(event) => setErledigt(event.target.checked)}
              />
            )}
            label="Erledigt"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose} color="secondary" startIcon={<Close />}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<Save />}
          disabled={saving || !mandantId || !betriebId || !customerId || !bestelldatum || !bestellung.trim()}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BestellungDialog;
